// src/pages/projects/ProjectList.jsx

import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { fetchProjectsForTechnician } from "./projectService";

const STATUS_COLORS = {
  active:    "bg-green-100 text-green-700",
  on_hold:   "bg-yellow-100 text-yellow-700",
  completed: "bg-blue-100 text-blue-700",
  cancelled: "bg-red-100 text-red-700",
};

const TYPE_COLORS = {
  installation: "bg-purple-100 text-purple-700",
  amc:          "bg-cyan-100 text-cyan-700",
  maintenance:  "bg-orange-100 text-orange-700",
  supply:       "bg-gray-100 text-gray-700",
};

export default function ProjectList() {
  const navigate   = useNavigate();
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";
  // Office roles see every company project plus money figures; field roles
  // (site engineer, technician) see only projects assigned to them.
  const isManager  = ["sm", "owner", "manager"].includes(user.role);

  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("all"); // all|active|completed|on_hold

  useEffect(() => { fetchProjects(); }, []); // eslint-disable-line

  const fetchProjects = async () => {
    try {
      setLoading(true);

      // Technicians only see projects they actually have assigned work in —
      // never the full company project list.
      if (user.role === "technician") {
        const scoped = await fetchProjectsForTechnician(company_id, user.uid || user.id);
        scoped.sort((a, b) => {
          const aT = a.created_at?.seconds || 0;
          const bT = b.created_at?.seconds || 0;
          return bT - aT;
        });
        setProjects(scoped);
        return;
      }

      const q = query(
        collection(db, "projects"),
        where("company_id", "==", company_id)
      );
      const snap = await getDocs(q);
      let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Site Engineers only see projects they are assigned to
      // (projects.site_engineer_ids) — same matching MySiteConfirmations.js
      // already uses, with uid || id.
      if (user.role === "site_engineer") {
        const myUid = user.uid || user.id;
        list = list.filter(p => (p.site_engineer_ids || []).includes(myUid));
      }

      // Sort by created_at desc client-side
      list.sort((a, b) => {
        const aT = a.created_at?.seconds || 0;
        const bT = b.created_at?.seconds || 0;
        return bT - aT;
      });
      setProjects(list);
    } catch (err) {
      console.error("fetchProjects error:", err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = projects.filter(p => {
    const matchSearch = !search ||
      p.project_name?.toLowerCase().includes(search.toLowerCase()) ||
      p.client_name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || p.status === filter;
    return matchSearch && matchFilter;
  });

  // Summary counts
  const counts = {
    all:       projects.length,
    active:    projects.filter(p => p.status === "active").length,
    on_hold:   projects.filter(p => p.status === "on_hold").length,
    completed: projects.filter(p => p.status === "completed").length,
  };

  const totalValue = projects
    .filter(p => p.status === "active")
    .reduce((s, p) => s + Number(p.total_value || 0), 0);

  if (loading) return <div className="p-6 text-gray-500">Loading projects...</div>;

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-6xl mx-auto">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Projects</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {counts.active} active{isManager && ` · ₹${totalValue.toLocaleString("en-IN")} pipeline`}
          </p>
        </div>
        {isManager && (
          <button
            onClick={() => navigate("/projects/create")}
            className="px-4 py-2 bg-green-600 text-white rounded-xl font-semibold text-sm hover:bg-green-700">
            + New Project
          </button>
        )}
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total",     key: "all",       color: "bg-gray-800" },
          { label: "Active",    key: "active",    color: "bg-green-600" },
          { label: "On Hold",   key: "on_hold",   color: "bg-yellow-500" },
          { label: "Completed", key: "completed", color: "bg-blue-600" },
        ].map(c => (
          <button key={c.key}
            onClick={() => setFilter(c.key)}
            className={`${c.color} text-white rounded-xl p-4 text-left transition hover:opacity-90 ${
              filter === c.key ? "ring-2 ring-white/50 ring-offset-1" : ""
            }`}>
            <div className="text-2xl font-bold">{counts[c.key]}</div>
            <div className="text-sm opacity-80">{c.label}</div>
          </button>
        ))}
      </div>

      {/* SEARCH */}
      <input
        type="text"
        placeholder="Search by project name or client..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-green-300"
      />

      {/* PROJECT LIST */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">📋</div>
          <p className="font-semibold">No projects found</p>
          <p className="text-sm mt-1">{isManager ? "Create your first project" : "No projects are assigned to you yet"}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(p => (
            <div key={p.id}
              onClick={() => navigate(`/projects/${p.id}`)}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md cursor-pointer transition">

              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-800 text-base">
                      {p.project_name}
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      STATUS_COLORS[p.status] || "bg-gray-100 text-gray-600"
                    }`}>
                      {p.status?.replace("_", " ").toUpperCase()}
                    </span>
                    {p.project_type && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        TYPE_COLORS[p.project_type] || "bg-gray-100 text-gray-600"
                      }`}>
                        {p.project_type}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{p.client_name || "—"}</p>
                  {p.site_name && (
                    <p className="text-xs text-gray-400">{p.site_name}</p>
                  )}
                </div>

                <div className="text-right shrink-0">
                  {isManager && p.total_value > 0 && (
                    <div className="text-green-700 font-bold text-base">
                      ₹{Number(p.total_value).toLocaleString("en-IN")}
                    </div>
                  )}
                  {p.start_date && (
                    <div className="text-xs text-gray-400 mt-1">
                      Start: {p.start_date}
                    </div>
                  )}
                  {p.end_date && (
                    <div className="text-xs text-gray-400">
                      End: {p.end_date}
                    </div>
                  )}
                </div>
              </div>

              {p.description && (
                <p className="text-xs text-gray-400 mt-2 line-clamp-1">{p.description}</p>
              )}

            </div>
          ))}
        </div>
      )}
    </div>
  );
}